// Deterministic SVG placeholder generator. When an asset is missing from the
// manifest (HOO-46), callers render one of these instead of a blank frame.
// Output is a data URL ("data:image/svg+xml;utf8,...") so it can go directly
// into an <img src=""> without a separate HTTP round trip.
//
// Determinism: the same (kind, name) pair always produces the same image.
// The color is seeded by hash(name) so Margthe always gets the same tint,
// making different tokens of the same entity visually stable.

export type PlaceholderKind = "scene" | "npc" | "mob" | "item" | "icon";

/** 32-bit FNV-1a hash — fast, stable, non-cryptographic. */
function hash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** HSL tint derived from name hash, in a muted palette range. */
function paletteFor(name: string, kind: PlaceholderKind): { bg: string; fg: string; accent: string } {
  const h = hash(name + ":" + kind);
  const hue = h % 360;
  const sat = 30 + ((h >>> 8) % 25);   // 30-55%
  const lite = 25 + ((h >>> 16) % 15); // 25-40% (dark bg)
  return {
    bg: `hsl(${hue} ${sat}% ${lite}%)`,
    fg: `hsl(${hue} ${sat - 10}% ${Math.min(88, lite + 55)}%)`,
    accent: `hsl(${(hue + 40) % 360} ${sat + 10}% ${lite + 20}%)`,
  };
}

/** Emoji or glyph chosen by category for iconic hints. */
function glyphFor(kind: PlaceholderKind, category: string): string {
  const c = category.toLowerCase();
  if (kind === "item" || kind === "icon") {
    if (/weapon|sword|dagger|axe|mace|hammer/.test(c)) return "⚔";
    if (/armor|chest|helm|head|shield/.test(c)) return "🛡";
    if (/potion|consumable|elixir|flask/.test(c)) return "⚗";
    if (/food|drink|ration/.test(c)) return "🍞";
    if (/book|scroll|tome|parchment/.test(c)) return "📜";
    if (/herb|flower|reagent|plant/.test(c)) return "⚘";
    if (/ring|amulet|jewelry|gem/.test(c)) return "◈";
    if (/key/.test(c)) return "🗝";
    if (/quest/.test(c)) return "❗";
    if (/coin|gold|currency/.test(c)) return "◉";
    return "▣";
  }
  if (kind === "npc") {
    if (/smith|blacksmith/.test(c)) return "⚒";
    if (/priest|cleric/.test(c)) return "✝";
    if (/merchant|vendor|trader/.test(c)) return "⚖";
    if (/witch|hag|crone/.test(c)) return "☾";
    if (/guard|knight|warrior/.test(c)) return "⚔";
    return "☺";
  }
  if (kind === "mob") {
    if (/wolf|dog|hound/.test(c)) return "🐺";
    if (/bear/.test(c)) return "🐻";
    if (/spider/.test(c)) return "🕷";
    if (/snake|serpent/.test(c)) return "🐍";
    if (/bird|raven|crow/.test(c)) return "🦅";
    if (/skeleton|undead/.test(c)) return "☠";
    if (/demon|devil/.test(c)) return "△";
    if (/orc|goblin|troll/.test(c)) return "⚒";
    return "◇";
  }
  // scene
  if (/village|town/.test(c)) return "⌂";
  if (/forest|wood|grove/.test(c)) return "♣";
  if (/cave|dungeon|crypt/.test(c)) return "▲";
  if (/mountain|peak/.test(c)) return "△";
  if (/water|river|sea|lake/.test(c)) return "≈";
  if (/temple|church|cathedral/.test(c)) return "✝";
  return "◎";
}

export interface PlaceholderOptions {
  kind: PlaceholderKind;
  name: string;
  /** Influences glyph choice and short label. */
  category?: string;
  /** Output square size. Default 256. */
  size?: number;
}

/**
 * Build an SVG string for a placeholder. Safe to embed in a data URL.
 * Returned string is NOT URL-encoded — use placeholderDataUrl() for that.
 */
export function placeholderSvg(opt: PlaceholderOptions): string {
  const size = opt.size ?? 256;
  const { bg, fg, accent } = paletteFor(opt.name, opt.kind);
  const glyph = glyphFor(opt.kind, opt.category ?? "");
  const initial = (opt.name.trim()[0] ?? "?").toUpperCase();
  const label = opt.name.length > 18 ? opt.name.slice(0, 16) + "…" : opt.name;

  // NPCs: circular frame with initial; Mobs: hex; Items/icons: rounded square; Scenes: landscape bands.
  let inner = "";
  if (opt.kind === "scene") {
    inner = `
      <rect x="0" y="0" width="${size}" height="${size}" fill="${bg}"/>
      <rect x="0" y="${size * 0.6}" width="${size}" height="${size * 0.4}" fill="${accent}" opacity="0.55"/>
      <circle cx="${size * 0.18}" cy="${size * 0.22}" r="${size * 0.08}" fill="${fg}" opacity="0.4"/>
      <text x="${size / 2}" y="${size / 2}" font-family="serif" font-size="${size * 0.35}" text-anchor="middle" dominant-baseline="central" fill="${fg}" opacity="0.75">${glyph}</text>
      <text x="${size / 2}" y="${size * 0.88}" font-family="system-ui,sans-serif" font-size="${size * 0.075}" text-anchor="middle" fill="${fg}" opacity="0.9">${escXml(label)}</text>
    `;
  } else if (opt.kind === "npc") {
    inner = `
      <rect x="0" y="0" width="${size}" height="${size}" fill="${bg}"/>
      <circle cx="${size / 2}" cy="${size * 0.45}" r="${size * 0.32}" fill="${accent}" opacity="0.6"/>
      <text x="${size / 2}" y="${size * 0.5}" font-family="serif" font-weight="bold" font-size="${size * 0.32}" text-anchor="middle" dominant-baseline="central" fill="${fg}">${initial}</text>
      <text x="${size / 2}" y="${size * 0.85}" font-family="system-ui,sans-serif" font-size="${size * 0.085}" text-anchor="middle" fill="${fg}">${escXml(label)}</text>
      <text x="${size * 0.82}" y="${size * 0.22}" font-size="${size * 0.14}" text-anchor="middle" fill="${fg}" opacity="0.85">${glyph}</text>
    `;
  } else if (opt.kind === "mob") {
    const r = size * 0.38;
    const cx = size / 2, cy = size * 0.48;
    const pts = [0, 60, 120, 180, 240, 300].map((a) => {
      const rad = (a * Math.PI) / 180;
      return `${(cx + r * Math.cos(rad)).toFixed(1)},${(cy + r * Math.sin(rad)).toFixed(1)}`;
    }).join(" ");
    inner = `
      <rect x="0" y="0" width="${size}" height="${size}" fill="${bg}"/>
      <polygon points="${pts}" fill="${accent}" opacity="0.7"/>
      <text x="${cx}" y="${cy}" font-size="${size * 0.36}" text-anchor="middle" dominant-baseline="central" fill="${fg}">${glyph}</text>
      <text x="${size / 2}" y="${size * 0.92}" font-family="system-ui,sans-serif" font-size="${size * 0.085}" text-anchor="middle" fill="${fg}">${escXml(label)}</text>
    `;
  } else {
    // item / icon: rounded square with glyph + initial badge
    const pad = size * 0.08;
    inner = `
      <rect x="${pad}" y="${pad}" width="${size - 2 * pad}" height="${size - 2 * pad}" rx="${size * 0.08}" fill="${bg}" stroke="${fg}" stroke-width="${size * 0.015}"/>
      <text x="${size / 2}" y="${size * 0.48}" font-size="${size * 0.45}" text-anchor="middle" dominant-baseline="central" fill="${fg}" opacity="0.9">${glyph}</text>
      <text x="${size / 2}" y="${size * 0.88}" font-family="system-ui,sans-serif" font-size="${size * 0.08}" text-anchor="middle" fill="${fg}" opacity="0.85">${escXml(label)}</text>
    `;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">${inner}</svg>`;
}

/** Return a `data:image/svg+xml;utf8,...` URL, safe for <img src="">. */
export function placeholderDataUrl(opt: PlaceholderOptions): string {
  const svg = placeholderSvg(opt);
  // Only need to encode characters that break in URL / attribute contexts.
  const encoded = svg
    .replace(/%/g, "%25")
    .replace(/#/g, "%23")
    .replace(/"/g, "'")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E")
    .replace(/\s+/g, " ")
    .trim();
  return `data:image/svg+xml;utf8,${encoded}`;
}

function escXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}
